const destinations = [
  {
    name: "Germany",
    context: "McConnell AFB families with PCS orders to Ramstein AB and Spangdahlem AB; Spirit AeroSystems and Textron Aviation engineers visiting suppliers and customers in the European aerospace corridor; Koch Industries executives with European operations",
    urgency: "PCS report dates and aerospace supplier audits have fixed, non-negotiable dates — a delayed passport means a missed report date or a grounded investigation",
    icon: "ri-map-pin-2-line",
    flag: "DE",
  },
  {
    name: "United Kingdom",
    context: "Koch Industries board meetings and subsidiary visits in London; Textron Aviation and Bombardier Learjet customer demonstrations; WSU faculty conferences and research collaborations with UK universities",
    urgency: "Board meetings and customer demos are scheduled months in advance and cannot be rescheduled for one attendee's passport issue",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "France",
    context: "Spirit AeroSystems A350 and A320 component deliveries and quality investigations at Airbus facilities in Toulouse; aerospace engineering conferences at Le Bourget; WSU study-abroad programs",
    urgency: "A quality investigation at an Airbus assembly line halts aircraft production — Spirit engineers must be on-site within days, not weeks",
    icon: "ri-map-pin-2-line",
    flag: "FR",
  },
  {
    name: "Japan & South Korea",
    context: "Textron Aviation and Spirit AeroSystems Asia-Pacific customer support; McConnell AFB families with PCS orders to Yokota AB and Osan AB; Koch Industries Asia-Pacific operations",
    urgency: "Military PCS orders and aerospace customer commitments have fixed schedules that do not flex for passport delays",
    icon: "ri-map-pin-2-line",
    flag: "JP",
  },
  {
    name: "Canada & Mexico",
    context: "Aerospace supply chain visits to Bombardier facilities in Montreal; Koch Industries North American operations; WSU spring break and summer study-abroad programs in Latin America",
    urgency: "Seasonal study-abroad programs and quarterly business reviews operate on fixed calendars — missing a deadline means waiting another year",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
  {
    name: "Brazil",
    context: "Embraer supplier relationships and aerospace trade missions from Wichita's aviation cluster; Koch Industries South American operations; WSU exchange programs with Brazilian universities",
    urgency: "Brazilian visa requirements add 2–4 weeks of processing time — your passport must be valid well before the visa application even begins",
    icon: "ri-map-pin-2-line",
    flag: "BR",
  },
];

const commonSituations = [
  {
    stat: "360 Miles",
    label: "distance to the nearest regional passport agency in Dallas — a 10+ hour round-trip drive down I-35 for Wichita travelers",
  },
  {
    stat: "3–6 Weeks",
    label: "average notice Wichita travelers have before an international trip — whether it's PCS orders, supplier visits, or study-abroad orientation",
  },
  {
    stat: "40%",
    label: "of Wichita expedited requests are military and aerospace-related — PCS moves, overseas deployments, and aviation business travel",
  },
  {
    stat: "4 Days",
    label: "shortest turnaround we have accommodated for a Spirit AeroSystems engineer called to an urgent A350 quality investigation at Airbus in Toulouse",
  },
];

export default function WichitaDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Wichita Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from South Central Kansas clients. Understanding local travel behavior — from Air Force PCS cycles to aerospace production schedules — is part of why our document review catches issues before they derail your deployment, business trip, or vacation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Kansas &amp; Regional Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Kansas and the surrounding region with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/overland-park" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Overland Park <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/topeka" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Topeka <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/kansas-city" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Kansas City <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/oklahoma-city" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Oklahoma City <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}